/**
 * JPBilingualSections — presentational bilingual (English / 日本語) renderer for
 * a JP PMDA package-insert label.
 *
 * This is the render-with-toggle core extracted from ``JPSectionView`` so it can
 * be reused with PRE-FETCHED data (the Compare view already fetches per panel and
 * must NOT re-fetch through the drug-detail JPSectionView).  It owns its OWN
 * ``lang`` state, so every instance (e.g. each Compare panel) toggles English /
 * 日本語 independently.  English is the default.
 *
 * It is purely presentational: the caller hands it the already-loaded
 * ``doc`` / ``sections`` / ``figures`` (the exact shapes returned by
 * ``GET /drugs/{id}/jp-sections``).  ``JPSectionView`` is now a thin fetch +
 * TOC-event wrapper around this component (drug-detail behaviour unchanged).
 */
import React, { useState } from 'react';
import { highlightHtmlString } from '../SplContentRenderer';
import { FIG_RE, injectFigures } from '../../utils/jpFigures';

export { injectFigures };

export interface JPSection {
  section_number: string;
  section_type: string;
  is_parent: boolean;
  title: string;
  content_html: string | null;
  title_ja: string | null;
  content_html_ja: string | null;
  table_count: number;
  figure_count: number;
}

export interface JPFigure {
  gfname: string;
  kind: string | null;
  gcs_uri: string | null;
  /** Public GCS URL — loadable directly by <img> (bucket is public). */
  image_url: string | null;
  caption: string | null;
  caption_ja: string | null;
}

export interface JPDoc {
  id: number;
  brand_name: string | null;
  brand_name_en: string | null;
  generic_name: string | null;
  manufacturer: string | null;
  therapeutic_class: string | null;
  revision_date: string | null;
  yj_code: string | null;
  version_number?: number | null;
  is_current?: boolean;
  package_insert_no?: string | null;
  lineage_key?: string | null;
  retired_marker?: string | null;
}

export type JPLang = 'en' | 'ja';

/** Identifier the parent TOC + scroll handler use to find a section. */
export const jpSectionDomId = (sectionNumber: string) => `jp-sec-${sectionNumber}`;

/** gfnames referenced inline anywhere (both languages). */
function referencedGfnames(sections: JPSection[]): Set<string> {
  const set = new Set<string>();
  for (const s of sections) {
    for (const h of [s.content_html, s.content_html_ja]) {
      if (!h) continue;
      for (const m of h.match(FIG_RE) || []) set.add(m.slice(3, -1));
    }
  }
  return set;
}

/** Group the flat ordered section list into top-level sections with
 *  their subsection children (§9 → §9.1, §9.2, …). */
function buildTree(sections: JPSection[]): { top: JPSection; children: JPSection[] }[] {
  const tops = sections.filter((s) => !s.section_number.includes('.'));
  return tops.map((top) => ({
    top,
    children: sections.filter((s) => s.section_number.startsWith(top.section_number + '.')),
  }));
}

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <span
    className={`inline-block text-slate-400 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
    aria-hidden
  >
    ▶
  </span>
);

/** Styles for the rendered JP HTML — shared by drug-detail + Compare. */
export const JP_CONTENT_STYLE = `
  .jp-content table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; font-size: 0.8rem; }
  .jp-content th, .jp-content td { border: 1px solid #e2e8f0; padding: 4px 8px; text-align: left; vertical-align: top; }
  .jp-content th { background: #f8fafc; font-weight: 600; }
  .jp-content { overflow-x: auto; }
  .jp-figure { margin: 0.75rem 0; text-align: center; }
  .jp-figure img { max-width: 100%; height: auto; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; }
  .jp-fig-missing { color: #94a3b8; font-style: italic; font-size: 0.8rem; }
  .jp-content ol, .jp-content ul { margin: 0.4rem 0; padding-left: 1.5rem; }
  .jp-content ol.jp-list { list-style: decimal; }
  .jp-content ul.jp-list { list-style: disc; }
  .jp-content li { margin: 0.25rem 0; line-height: 1.5; }
  .jp-content li > ol, .jp-content li > ul { margin: 0.3rem 0; }
  .jp-content ol.jp-refs { font-size: 0.82rem; color: #475569; }
  .jp-content p { margin: 0.4rem 0; line-height: 1.55; }
  .jp-content sup { font-size: 0.7em; vertical-align: super; }
  .jp-content sub { font-size: 0.7em; vertical-align: sub; }
  .jp-tbl-caption { font-size: 0.8rem; font-weight: 600; margin: 0.3rem 0; }
  .jp-tbl-foot { font-size: 0.75rem; color: #64748b; margin-top: 0.2rem; }
`;

const JPBilingualSections: React.FC<{
  doc: JPDoc | null;
  sections: JPSection[];
  figures: JPFigure[];
  /** Initial expanded section numbers (drug-detail drives this via TOC
   *  events; Compare leaves it empty → all collapsed). */
  expanded: Set<string>;
  /** Toggle a section open/closed (lifted so the parent can also drive
   *  expand-all / TOC-scroll). */
  onToggle: (num: string) => void;
  /** Optional per-section attributes (id + data-section) so a parent can
   *  scroll to / anchor sections.  Drug-detail uses the jp-sec-* ids; Compare
   *  passes nothing (the accordion is its own nav). */
  withSectionAnchors?: boolean;
  /** Restrict the rendered top-level sections to this set of section numbers
   *  (+ their subsection families).  ``undefined`` = show all.  Compare uses
   *  this to honour a per-panel section selection. */
  sectionFilter?: string[];
  /** When true the EN/日本語 toggle floats sticky at the top-right (drug-detail
   *  layout).  Compare passes false and renders the toggle inline in its panel
   *  header instead, via ``lang`` + ``onLangChange`` below. */
  stickyToggle?: boolean;
  /** Suppress the in-body document header (brand name + manufacturer/YJ line).
   *  Compare already shows that in its panel header, so it'd be duplicated. */
  hideDocHeader?: boolean;
  /** Controlled language (Compare wires this to its own per-panel toggle).
   *  Omit to let the component own its language internally (drug-detail). */
  lang?: JPLang;
  onLangChange?: (l: JPLang) => void;
  /** Search terms to box (<mark>) inside the rendered content — keyword
   *  Compare passes the per-row colored terms.  Reuses the exact SPL-viewer
   *  highlighter so JP matches every other region. */
  highlightTerms?: string[];
  englishExact?: boolean;
  highlightColors?: (string | undefined)[];
  /** Cogni: the AI evidence chunk to wrap in the blue `.evidence-chunk-highlight`
   *  block — anchor-matched the same way the SPL viewer does. */
  evidenceChunkText?: string | null;
  /** A5 full-label view: every section/subsection body is mounted and the
   *  headings are plain (non-interactive) h3/h4; ``expanded``/``onToggle``
   *  are ignored. */
  alwaysOpen?: boolean | undefined;
}> = ({
  doc,
  sections,
  figures,
  expanded,
  onToggle,
  withSectionAnchors = true,
  sectionFilter,
  stickyToggle = true,
  hideDocHeader = false,
  lang: controlledLang,
  onLangChange,
  highlightTerms,
  highlightColors,
  englishExact = false,
  evidenceChunkText,
  alwaysOpen = false,
}) => {
  const [internalLang, setInternalLang] = useState<JPLang>('en');
  const lang = controlledLang ?? internalLang;
  const setLang = (l: JPLang) => {
    if (onLangChange) onLangChange(l);
    else setInternalLang(l);
  };

  // gfname → public GCS URL, straight from the response.
  const figUrls: Record<string, string> = {};
  for (const f of figures) if (f.image_url) figUrls[f.gfname] = f.image_url;

  const referenced = referencedGfnames(sections);
  const unreferenced = figures.filter((f) => !referenced.has(f.gfname));
  let tree = buildTree(sections);
  // Honour a per-panel section selection: keep only top-level sections whose
  // number (or one of whose subsections) is in the filter.
  if (sectionFilter && sectionFilter.length) {
    const wanted = new Set(sectionFilter);
    // EXACT membership: a subsection shows only if IT is selected; a top-level
    // section shows if its own number is selected OR at least one of its
    // subsections is. Selecting the parent does NOT force every child visible —
    // unchecking §3.1 must hide §3.1 even while §3 / §3.2 stay checked.
    tree = tree
      .map(({ top, children }) => ({
        top,
        children: children.filter((c) => wanted.has(c.section_number)),
      }))
      .filter(({ top, children }) => wanted.has(top.section_number) || children.length > 0);
  }

  const titleOf = (s: JPSection) => (lang === 'en' ? s.title : (s.title_ja || s.title));
  const hasHL = (highlightTerms && highlightTerms.length > 0) || !!evidenceChunkText;
  const htmlOf = (s: JPSection) => {
    const raw = lang === 'en' ? s.content_html : (s.content_html_ja || s.content_html);
    if (!raw) return null;
    const withFigs = injectFigures(raw, figUrls);
    // Term + evidence-chunk highlighting, reusing the SPL viewer's exact
    // helpers so JP gets the same <mark> boxes / blue evidence block as every
    // other region's Compare panel.  Only the section containing the evidence
    // chunk will anchor-match the blue block; terms box wherever they appear.
    return hasHL
      ? highlightHtmlString(withFigs, highlightTerms ?? [], highlightColors, evidenceChunkText, { englishExact: englishExact && lang === 'en' })
      : withFigs;
  };

  const contentBlock = (s: JPSection) => {
    const html = htmlOf(s);
    return html
      ? <div className="mt-1 mb-1 text-sm text-slate-700 jp-content"
             dangerouslySetInnerHTML={{ __html: html }} />
      : <div className="mt-1 mb-1 text-xs text-slate-400 italic">
          {(s.table_count > 0 || s.figure_count > 0)
            ? `(${s.table_count} table(s), ${s.figure_count} figure(s))`
            : '(no text content)'}
        </div>;
  };

  const langToggle = (
    <div className="inline-flex rounded-full border border-slate-200 bg-white/95 shadow-md backdrop-blur px-0.5 py-0.5 text-xs">
      {(['en', 'ja'] as JPLang[]).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          className={
            'px-3 py-1 rounded-full transition-colors ' +
            (lang === l
              ? 'bg-indigo-600 text-white font-medium shadow-sm'
              : 'text-slate-500 hover:text-slate-700')
          }
          aria-pressed={lang === l}
        >
          {l === 'en' ? 'English' : '日本語'}
        </button>
      ))}
    </div>
  );

  return (
    <div className="jp-section-view relative">
      <style>{JP_CONTENT_STYLE}</style>

      {/* Sticky floating language toggle — stays visible while scrolling.
          Compare renders its own toggle in the panel header (stickyToggle=false). */}
      {stickyToggle && (
        <div className="sticky top-2 z-20 flex justify-end pointer-events-none -mb-9">
          <div className="pointer-events-auto">{langToggle}</div>
        </div>
      )}

      {/* Document header */}
      {doc && !hideDocHeader && (
        <div className={`mb-3 pb-3 border-b border-slate-200 ${stickyToggle ? 'pr-28' : ''}`}>
          <div className="text-base font-semibold text-slate-800">
            {lang === 'en'
              ? (doc.brand_name_en || doc.brand_name || doc.yj_code)
              : (doc.brand_name || doc.brand_name_en || doc.yj_code)}
          </div>
          <div className="text-xs text-slate-500">
            {doc.generic_name} · {doc.manufacturer}
            {doc.revision_date ? ` · rev ${doc.revision_date}` : ''} · YJ {doc.yj_code}
            {doc.package_insert_no ? ` · PIN ${doc.package_insert_no}` : ''}
          </div>
        </div>
      )}

      {/* Accordion */}
      <div className="border-t border-slate-200">
        {tree.map(({ top, children }) => {
          const isOpen = alwaysOpen || expanded.has(top.section_number);
          return (
            <section
              key={top.section_number}
              {...(withSectionAnchors ? { id: jpSectionDomId(top.section_number) } : {})}
              data-section={top.section_number}
              className="border-b border-slate-200"
            >
              {alwaysOpen ? (
                <h3 className="w-full flex items-center gap-2 py-3 text-left">
                  <span className="text-sm font-semibold text-slate-800">
                    <span className="text-slate-400 mr-2">§{top.section_number}</span>
                    {titleOf(top)}
                  </span>
                </h3>
              ) : (
              <button
                type="button"
                onClick={() => onToggle(top.section_number)}
                className="w-full flex items-center gap-2 py-3 text-left hover:bg-slate-50"
                aria-expanded={isOpen}
              >
                <Chevron open={isOpen} />
                <span className="text-sm font-semibold text-slate-800">
                  <span className="text-slate-400 mr-2">§{top.section_number}</span>
                  {titleOf(top)}
                </span>
              </button>
              )}

              {isOpen && (
                <div className="pb-3 pl-6">
                  {contentBlock(top)}
                  {children.map((sub) => {
                    const subOpen = alwaysOpen || expanded.has(sub.section_number);
                    return (
                      <div
                        key={sub.section_number}
                        {...(withSectionAnchors ? { id: jpSectionDomId(sub.section_number) } : {})}
                        data-section={sub.section_number}
                        className="border-t border-slate-100"
                      >
                        {alwaysOpen ? (
                          <h4 className="w-full flex items-center gap-2 py-2 text-left">
                            <span className="text-[13px] font-medium text-slate-700">
                              <span className="text-slate-400 mr-2">§{sub.section_number}</span>
                              {titleOf(sub)}
                            </span>
                          </h4>
                        ) : (
                        <button
                          type="button"
                          onClick={() => onToggle(sub.section_number)}
                          className="w-full flex items-center gap-2 py-2 text-left hover:bg-slate-50"
                          aria-expanded={subOpen}
                        >
                          <Chevron open={subOpen} />
                          <span className="text-[13px] font-medium text-slate-700">
                            <span className="text-slate-400 mr-2">§{sub.section_number}</span>
                            {titleOf(sub)}
                          </span>
                        </button>
                        )}
                        {subOpen && <div className="pb-2 pl-6">{contentBlock(sub)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Figures with no inline placeholder — shown so nothing is dropped. */}
      {unreferenced.length > 0 && (!sectionFilter || !sectionFilter.length) && (
        <section
          {...(withSectionAnchors ? { id: jpSectionDomId('FIG') } : {})}
          className="mt-6 pt-4 border-t border-slate-200"
        >
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Figures</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {unreferenced.map((f) => (
              <figure key={f.gfname} className="jp-figure">
                {figUrls[f.gfname]
                  ? <img src={figUrls[f.gfname]} alt={f.gfname} loading="lazy"
                         className="max-w-full h-auto border border-slate-200 rounded bg-white" />
                  : <span className="jp-fig-missing">［図: {f.gfname}］</span>}
                {(lang === 'en' ? f.caption : (f.caption_ja || f.caption)) && (
                  <figcaption className="mt-1 text-xs text-slate-500">
                    {lang === 'en' ? f.caption : (f.caption_ja || f.caption)}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default JPBilingualSections;
